import React, { useState } from 'react';
import { Ban, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { blackoutsApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';

// CHANGE OR REMOVE THE NO-LEAVE DATES YOU ADDED (Rob 1C + 2A, 02/11/2026). Managers can add them from
// My leave and the Team calendar, but the list with Edit / Delete lives in Settings, which only admins
// reach — so a manager couldn't undo their own. Two places, both from this file so they can't drift:
//   * <NoLeaveDatesYours> — the "Yours" tab of the No-leave dates box (works on a phone);
//   * <NoLeaveDateActions> — Change / Remove in the card on a no-leave day.
// ⚠️ Which ones: the ones YOU added that the SERVER says you may change (`can_change`, worked out by
// routers/blackouts._change_refusal — the same rule as the change and the delete themselves).

export function mine(blackouts, meId) {
  return (blackouts || []).filter((b) => b.can_change && b.created_by_id && b.created_by_id === meId);
}

function who(b) {
  if (b.scope === 'company') return 'Everyone';
  if (b.scope === 'department') return b.department_name || 'A department';
  return b.user_name || 'One person';
}

function when(b) {
  return b.start_date === b.end_date
    ? formatDateGB(b.start_date)
    : `${formatDateGB(b.start_date)} – ${formatDateGB(b.end_date)}`;
}

// Remove asks once more on the same button — no browser pop-up (they block the page).
function RemoveButton({ blackout, onRemoved, small = false }) {
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!sure) { setSure(true); return; }
    setBusy(true);
    try {
      await blackoutsApi.delete(blackout.id);
      toast.success('No-leave dates removed');
      onRemoved && onRemoved(blackout);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Those no-leave dates couldn't be removed");
      setSure(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={go}
      onBlur={() => setSure(false)}
      disabled={busy}
      data-testid={`no-leave-remove-${blackout.id}`}
      className={`${small ? 'text-[11px] px-2 py-0.5 rounded-md' : 'text-[12.5px] px-2.5 py-1.5 rounded-lg'} font-bold border transition-colors ${
        sure
          ? 'bg-hive-danger-fg text-white border-hive-danger-fg'
          : 'bg-card text-hive-danger-fg border-hive-danger-fg/30 hover:bg-hive-danger-bg dark:text-red-300'}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (sure ? 'Sure? Remove' : 'Remove')}
    </button>
  );
}

function ChangeButton({ blackout, onChange, small = false }) {
  return (
    <button
      type="button"
      onClick={() => onChange && onChange(blackout)}
      data-testid={`no-leave-change-${blackout.id}`}
      className={`${small ? 'text-[11px] px-2 py-0.5 rounded-md' : 'text-[12.5px] px-2.5 py-1.5 rounded-lg'} font-bold border border-border bg-card text-foreground hover:bg-secondary transition-colors`}
    >
      Change
    </button>
  );
}

export function NoLeaveDatesYours({ blackouts, meId, onChange, onRemoved }) {
  const rows = mine(blackouts, meId);
  return (
    <div data-testid="no-leave-yours">
      <p className="text-[13px] text-muted-foreground mb-3">
        The no-leave dates you added. Ones for the whole company, or added by somebody else, are
        changed by an admin in Settings.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">You haven&rsquo;t added any yet.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((b) => (
            <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-hive-line-soft bg-hive-tile dark:bg-secondary/60 dark:border-border">
              <div className="w-8 h-8 rounded-lg bg-hive-danger-bg dark:bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <Ban className="w-4 h-4 text-hive-danger-fg dark:text-red-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{b.name}</p>
                <p className="text-[12.5px] text-muted-foreground">{when(b)} · {who(b)}</p>
              </div>
              <ChangeButton blackout={b} onChange={onChange} />
              <RemoveButton blackout={b} onRemoved={onRemoved} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function NoLeaveDateActions({ blackout, meId, onChange, onRemoved }) {
  if (!blackout || !mine([blackout], meId).length) return null;
  return (
    <div className="flex gap-1.5 pt-2 mt-1 border-t border-border">
      <ChangeButton blackout={blackout} onChange={onChange} small />
      <RemoveButton blackout={blackout} onRemoved={onRemoved} small />
    </div>
  );
}
